import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "./config";
import { tokenStorage } from "./tokenStorage";

export interface TranscriptElement {
  id: number;
  time: string;
  speaker: string;
  text: string;
}

export interface KeyQuote {
  id: number;
  speaker: string;
  text: string;
  transcript_element: TranscriptElement;
}

export interface Sentiment {
  [key: string]: string;
}

export interface SummaryData {
  id?: number;
  transcript_id?: number;
  summary?: string;
  sentiment?: Sentiment[];
  created_at?: string;
  updated_at?: string;
}

export interface Section {
  id: number;
  title: string;
  summary: string;
  topics: string[];
  elements: TranscriptElement[];
  key_quotes: KeyQuote[];
  sentiment?: Sentiment[];
}

export interface ReportDetail {
  id: number;
  title?: string;
  transcript_id?: number;
  summary?: SummaryData;
  sentiment?: Sentiment[];
  created_at?: string;
  updated_at?: string;
  status?: string;
  transcript_elements?: TranscriptElement[];
  sections?: Section[];
}

export type JobStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
export type JobStep = "CREATED" | "EXTRACTING_AUDIO" | "AUDIO_EXTRACTED" | "TRANSCRIBING" | "TRANSCRIBED" | "PROCESSING_TRANSCRIPT";
export type JobType = "VIDEO" | "TRANSCRIPT";
export type Persona =
  | "auto"
  // Media
  | "podcaster"
  | "youtuber"
  | "journalist"
  // Education
  | "lecturer"
  | "curator"
  | "student"
  | "teacher"
  | "methodist"
  // Business
  | "business"
  | "consultant"
  | "ea_cos"
  | "executive"
  | "ld_training"
  | "manager"
  | "sales_cs"
  // Science
  | "journal_editor"
  | "peer_reviewer"
  | "phd_postdoc"
  | "researcher"
  | "sci_communicator"
  // Raw transcript
  | "transcript";

export interface ReportPreview {
  id: number;
  summary: string | null;
}

export interface ProcessingJobList {
  id: number;
  title: string;
  custom_title?: string | null;
  position: number;
  type: JobType;
  persona?: Persona;
  role?: number | null;
  role_slug?: string | null;
  role_name?: string | null;
  scenario?: number | null;
  scenario_slug?: string | null;
  scenario_name?: string | null;
  original_filename?: string | null;
  brief_data?: Record<string, unknown>;
  report_count?: number;
  status: JobStatus;
  step: JobStep;
  detected_language?: string;
  is_archived?: boolean;
  created_at: string;
  updated_at: string;
  report_preview: ReportPreview | null;
  report_labels?: Record<string, string>;
}

/** File Intelligence card stored on a job (see api/file_intelligence/builder.py).
 *  When extraction fails the backend stores `{ file, error, extraction_method: "none" }`. */
export interface FileMetadata {
  file?: string;
  error?: string;
  extraction_method?: string;
  verdict?: string[];
  verdict_human?: string;
  verdict_tip?: string;
  signals?: string[];
  date_confidence?: string;
  location?: {
    lat: number;
    lon: number;
    alt: number | null;
    accuracy: number | null;
    source: string | null;
    city_display?: string;
    city?: string;
    country?: string;
    display_name?: string;
  } | null;
  time?: {
    creation_utc: string | null;
    creation_local: string | null;
    filename_date: string | null;
    filename_time: string | null;
    date_confidence: string;
  };
  device?: {
    make: string | null;
    model: string | null;
    software: string | null;
    handler: string | null;
    content_id: string | null;
  };
  technical?: {
    resolution: string | null;
    width?: number | null;
    height?: number | null;
    codec: string | null;
    fps: string | null;
    bitrate_kbps: number | null;
    container: string | null;
    n_streams?: number | null;
    rotation?: string | null;
    duration_s: number | null;
    size_mb: number | null;
    [key: string]: unknown;
  };
  audio?: {
    codec_info: string | null;
    content_type: string | null;
    content_type_confidence: string | null;
  };
  filename_source?: string;
}

export interface JobInfo {
  filename: string;
  format: string;
  file_size_bytes: number;
  duration_seconds: number;
  speaker_count: number;
  language: string;
  reports_ready: number;
  reports_total: number;
  word_count: number;
  file_metadata?: FileMetadata | null;
}

export interface ProcessingJob extends ProcessingJobList {
  error_message: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = tokenStorage.getAccessToken();

  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    tokenStorage.clearTokensAndRedirectToLoginPage();
    throw new Error("Unauthorized");
  }

  return response;
}

/** Fetch a report rendered to a .docx file (server-side conversion), with JWT auth. */
/**
 * Tell the backend about a download it never handled.
 *
 * PDF and the bulk ZIP are assembled here in the browser, so no request reaches the API and
 * no server-side log can count them — "which formats do people take away" would answer with
 * its two largest contributors missing. Deliberately fire-and-forget: a metric must never
 * cost the user their download, so a failure here is swallowed.
 */
export async function reportDownload(
  format: string,
  meta?: { jobId?: number; reportType?: string },
): Promise<void> {
  try {
    await authFetch(`${API_BASE_URL}/api/downloads/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        format,
        job_id: meta?.jobId,
        report_type: meta?.reportType,
      }),
    });
  } catch {
    // Telemetry is not worth an error the user can see.
  }
}

export async function fetchReportDocx(reportId: number): Promise<{ blob: Blob; filename: string }> {
  const response = await authFetch(`${API_BASE_URL}/api/reports/${reportId}/download/docx/`);
  if (!response.ok) {
    throw new Error(`DOCX download failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  // Prefer RFC 5987 `filename*=UTF-8''…` (unicode-safe) over the ASCII `filename=`.
  let filename = `report_${reportId}.docx`;
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    filename = decodeURIComponent(utf8[1]);
  } else {
    const ascii = disposition.match(/filename="?([^";]+)"?/);
    if (ascii) filename = ascii[1];
  }
  return { blob, filename };
}

/** Fetch server-generated subtitles for a job's transcript (enriched speaker names,
 *  real timestamps), with JWT auth. `fmt` ∈ "srt" | "vtt" | "ass" | "txt". Source
 *  language only. The query param is `fmt`, not `format` (DRF reserves `format`). */
export async function fetchJobSubtitles(
  jobId: number,
  fmt: string,
): Promise<{ blob: Blob; filename: string }> {
  const response = await authFetch(
    `${API_BASE_URL}/jobs/${jobId}/subtitles/?fmt=${encodeURIComponent(fmt)}`,
  );
  if (!response.ok) {
    throw new Error(`Subtitle download failed (${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  let filename = `job-${jobId}.${fmt}`;
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    filename = decodeURIComponent(utf8[1]);
  } else {
    const ascii = disposition.match(/filename="?([^";]+)"?/);
    if (ascii) filename = ascii[1];
  }
  return { blob, filename };
}

export async function loginApi(username: string, password: string): Promise<AuthTokens> {
  const response = await fetch(`${API_BASE_URL}/auth/login/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || "Login failed");
  }

  return response.json();
}

export async function changePasswordApi(
  currentPassword: string,
  newPassword: string,
): Promise<AuthTokens> {
  const response = await authFetch(`${API_BASE_URL}/auth/change-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const fieldError =
      (Array.isArray(errorData.current_password) && errorData.current_password[0]) ||
      (Array.isArray(errorData.new_password) && errorData.new_password[0]) ||
      (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors[0]);
    throw new Error(fieldError || errorData.detail || "Password change failed");
  }

  return response.json();
}

export async function refreshTokenApi(refreshToken: string): Promise<{ access: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    throw new Error("Refresh failed");
  }

  return response.json();
}

export async function forgotPasswordApi(email: string): Promise<{ detail: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      (typeof errorData.detail === "string" && errorData.detail) ||
      (Array.isArray(errorData.email) && errorData.email[0]) ||
      "Something went wrong. Please try again.";
    throw new Error(message);
  }

  return response.json();
}

export async function resetPasswordApi(
  uid: string,
  token: string,
  newPassword: string,
): Promise<{ detail: string }> {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uid, token, new_password: newPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      (Array.isArray(errorData.detail) && errorData.detail[0]) ||
      (typeof errorData.detail === "string" && errorData.detail) ||
      (Array.isArray(errorData.new_password) && errorData.new_password[0]) ||
      "This reset link is invalid or has expired. Please request a new one.";
    throw new Error(message);
  }

  return response.json();
}

function parseUploadError(data: unknown): string {
  if (data && typeof data === "object") {
    const values = Object.values(data as Record<string, unknown>);
    for (const val of values) {
      if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string") {
        return val[0];
      }
      if (typeof val === "string") {
        return val;
      }
    }
  }
  return "The server rejected the upload. Please try again.";
}

export function useUploadTranscript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await authFetch(`${API_BASE_URL}/processing/transcript`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(parseUploadError(errorData));
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export interface UploadVideoArgs {
  file: File;
  persona?: Persona;
  keyTerms?: string;
  roleSlug?: string;
  scenarioId?: number | null;
  /** Brief form values (scenario brief_schema fields + context/language/key_terms). */
  briefData?: Record<string, unknown>;
}

export function useUploadVideo(onProgress?: (progress: number) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, persona, keyTerms, roleSlug, scenarioId, briefData }: UploadVideoArgs) => {
      const formData = new FormData();
      formData.append("file", file);
      if (persona) {
        formData.append("persona", persona);
      }
      if (keyTerms && keyTerms.trim()) {
        formData.append("key_terms", keyTerms.trim());
      }
      if (roleSlug) {
        formData.append("role_slug", roleSlug);
      }
      if (scenarioId != null) {
        formData.append("scenario_id", String(scenarioId));
      }
      if (briefData && Object.keys(briefData).length > 0) {
        formData.append("brief_data", JSON.stringify(briefData));
      }

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status === 401) {
            tokenStorage.clearTokensAndRedirectToLoginPage();
            reject(new Error("Unauthorized"));
            return;
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            let parsed: unknown = null;
            try {
              parsed = JSON.parse(xhr.responseText);
            } catch {}
            reject(new Error(parseUploadError(parsed)));
          }
        });

        xhr.addEventListener("error", () =>
          reject(
            new Error(
              "The connection dropped before the upload finished. Nothing was saved — check your internet and try again.",
            ),
          ),
        );

        xhr.open("POST", `${API_BASE_URL}/processing/video`);
        const token = tokenStorage.getAccessToken();
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
        xhr.send(formData);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export interface MeResponse {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
  new_flow_enabled: boolean;
  display_name: string;
  company: string;
  usage_limit_minutes: number;
  must_change_password: boolean;
}

/** Current user + profile-level flags (e.g. the new_flow_enabled feature flag). */
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/api/me/`);
      if (!response.ok) {
        throw new Error("Failed to fetch current user");
      }
      return response.json() as Promise<MeResponse>;
    },
    staleTime: 5 * 60_000,
  });
}

// --- Role catalog (public read-only) ---

export interface Vertical {
  id: number;
  name: string;
  slug: string;
  icon: string;
  description: string;
  sort_order: number;
  roles_count: number;
}

export interface RoleListItem {
  id: number;
  name: string;
  slug: string;
  vertical: string;
  vertical_slug: string;
  identity_type: string;
  description: string;
  role_context: string;
  is_preset: boolean;
  scenarios_count: number;
}

export function useVerticals() {
  return useQuery({
    queryKey: ["verticals"],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/api/verticals/`);
      if (!response.ok) {
        throw new Error("Failed to fetch verticals");
      }
      return response.json() as Promise<Vertical[]>;
    },
    staleTime: 5 * 60_000,
  });
}

/** Roles, optionally filtered by vertical slug (server-side ?vertical=). */
export function useRoles(verticalSlug?: string) {
  return useQuery({
    queryKey: ["roles", verticalSlug ?? "all"],
    queryFn: async () => {
      const qs = verticalSlug ? `?vertical=${encodeURIComponent(verticalSlug)}` : "";
      const response = await authFetch(`${API_BASE_URL}/api/roles/${qs}`);
      if (!response.ok) {
        throw new Error("Failed to fetch roles");
      }
      return response.json() as Promise<RoleListItem[]>;
    },
    staleTime: 5 * 60_000,
  });
}

export type BriefFieldType = "text" | "textarea" | "select" | "multiselect" | "toggle";

/** One field descriptor from a scenario's brief_schema (drives the dynamic brief form). */
export interface BriefField {
  key: string;
  label: string;
  type: BriefFieldType;
  options?: string[];
  required?: boolean;
  placeholder?: string;
}

export interface ScenarioDetail {
  id: number;
  name: string;
  slug: string;
  description: string;
  output_types: string[];
  brief_defaults: Record<string, unknown>;
  brief_schema: BriefField[];
  priority: string;
  is_active: boolean;
}

export interface IntentDetail {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_default: boolean;
  scenarios: ScenarioDetail[];
}

export interface IntentCategoryDetail {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
  intents: IntentDetail[];
}

export interface RoleDetail {
  id: number;
  name: string;
  slug: string;
  vertical: string;
  vertical_slug: string;
  identity_type: string;
  description: string;
  tier_a_fields: unknown[];
  is_preset: boolean;
  categories: IntentCategoryDetail[];
}

/** Role detail with nested categories → intents → scenarios. */
export function useRole(slug: string | undefined) {
  return useQuery({
    queryKey: ["role", slug],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/api/roles/${slug}/`);
      if (!response.ok) {
        throw new Error("Failed to fetch role");
      }
      return response.json() as Promise<RoleDetail>;
    },
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });
}

export interface UsageInfo {
  used: number;
  limit: number;
  plan: string;
  remaining: number;
  // Legacy keys kept for backward compatibility.
  used_minutes: number;
  limit_minutes: number;
}

export function useUsage() {
  return useQuery({
    queryKey: ["usage"],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/usage/`);
      if (!response.ok) {
        throw new Error("Failed to fetch usage");
      }
      return response.json() as Promise<UsageInfo>;
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useJobs() {
  return useQuery({
    queryKey: ["jobs"],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/jobs/`);
      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }
      return response.json() as Promise<ProcessingJobList[]>;
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (jobs?.some((job) => job.status === "PENDING" || job.status === "PROCESSING")) {
        return 10_000;
      }
      return false;
    },
  });
}

export function useRenameJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, title }: { jobId: number; title: string }) => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/rename/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) {
        throw new Error("Failed to rename project");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useArchiveJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/archive/`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error("Failed to archive project");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: number) => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/`, {
        method: "DELETE",
      });
      if (!response.ok && response.status !== 204) {
        throw new Error("Failed to delete project");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useJobInfo(jobId: number | null) {
  return useQuery({
    queryKey: ["job-info", jobId],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/info/`);
      if (!response.ok) {
        throw new Error("Failed to fetch job info");
      }
      return response.json() as Promise<JobInfo>;
    },
    enabled: jobId !== null,
  });
}

export function useReport(id: string | undefined, shouldPoll: boolean = false) {
  return useQuery({
    queryKey: ["reports", id],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/reports/${id}`);
      if (!response.ok) {
        throw new Error("Failed to fetch report");
      }
      return response.json() as Promise<ReportDetail>;
    },
    enabled: !!id,
    refetchInterval: shouldPoll ? 5_000 : false,
    retry: 1,
  });
}

// Prompt-based reports
export type PromptReportType =
  | "SUMMARY"
  | "TRANSCRIPT"
  | "ARTICLE"
  | "EXECUTIVE_BRIEF"
  | "KEY_QUOTES"
  | "TOPIC_MAP"
  | "SHOW_NOTES"
  | "BLOG_DRAFT"
  | "CHAPTERS"
  | "KEY_MOMENTS"
  | "DESCRIPTION_TAGS"
  | "FACT_CHECK"
  | "STUDY_NOTES"
  | "KEY_CONCEPTS"
  | "QUIZ"
  | "ACTION_ITEMS"
  | "DECISION_LOG"
  | "SENTIMENT"
  | "PLAIN_BRIEF";
export type PromptReportStatus = "CREATED" | "PROCESSING" | "PROCESSED" | "FAILED";

export interface PromptReportListItem {
  id: number;
  report_type: PromptReportType;
  status: PromptReportStatus;
  focus_mode?: "editing" | "quality" | "teaching" | null;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface PromptReportDetail extends PromptReportListItem {
  content: string;
  error_message: string;
}

export const PROMPT_REPORT_LABELS: Record<PromptReportType, string> = {
  SUMMARY: "Summary",
  TRANSCRIPT: "Transcript",
  ARTICLE: "Article Draft",
  EXECUTIVE_BRIEF: "Executive Brief",
  KEY_QUOTES: "Key Quotes",
  TOPIC_MAP: "Topic Map",
  SHOW_NOTES: "Show Notes",
  BLOG_DRAFT: "Blog Post Draft",
  CHAPTERS: "Chapters",
  KEY_MOMENTS: "Key Moments",
  DESCRIPTION_TAGS: "Description + Tags",
  FACT_CHECK: "Fact-Check Notes",
  STUDY_NOTES: "Study Notes",
  KEY_CONCEPTS: "Key Concepts",
  QUIZ: "Quiz Questions",
  ACTION_ITEMS: "Action Items",
  DECISION_LOG: "Decision Log",
  SENTIMENT: "Sentiment Analysis",
  PLAIN_BRIEF: "The Gist",
};

export function usePromptReports(jobId: string | undefined, jobStatus?: JobStatus, language?: string) {
  return useQuery({
    queryKey: ["prompt-reports", jobId, language],
    queryFn: async () => {
      const qs = language !== undefined ? `?language=${encodeURIComponent(language)}` : "";
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/reports/${qs}`);
      if (!response.ok) {
        throw new Error("Failed to fetch prompt reports");
      }
      return response.json() as Promise<PromptReportListItem[]>;
    },
    enabled: !!jobId,
    refetchInterval: (query) => reportListPollInterval(jobStatus, query.state.data),
  });
}

export const REPORT_POLL_INTERVAL_MS = 5_000;
/**
 * Consecutive failed fetches tolerated before a give-up-enabled poll stops.
 *
 * **Interim.** Only the Gist opts into giving up, and only because it is excluded from
 * the report list, leaving the client to probe a detail endpoint blind. Every value here
 * trades one bad outcome for another: too low strands a Gist still queued behind the rest
 * of the set, too high turns a permanently missing one into hundreds of 404s — which is
 * what a budget of 240 produced against a job whose Gist predates the keying fix.
 *
 * 12 keeps the noise to roughly a minute. The real repair is to stop probing: once the
 * report list carries PLAIN_BRIEF, the client polls only when a row is known to exist and
 * this constant stops mattering.
 */
export const MAX_REPORT_POLL_FAILURES = 12;

/**
 * How long until the next poll of a report detail, or `false` to stop.
 *
 * A 404 leaves `status` undefined, so a terminal state alone is not enough to stop —
 * and how long a 404 may legitimately last differs by call site, which is what
 * `giveUpOnRepeatedMisses` selects between.
 *
 * A report the list accounts for must be polled for as long as it takes: Refine
 * *deletes* the rows and regenerates them asynchronously, so its detail 404s for the
 * duration of an LLM run. Capping that leaves the tab spinning over a report the server
 * has already finished, recoverable only by reloading the page.
 *
 * The Gist is the opposite case and the reason the cap exists: it is excluded from the
 * report list, so nothing can ever confirm its 404 as temporary, and it used to be
 * requested every 5 seconds for as long as the tab stayed open. Callers that cannot
 * confirm a row exists opt in to giving up; everyone else keeps polling.
 */
export function reportPollInterval(
  shouldPoll: boolean,
  status: PromptReportStatus | undefined,
  fetchFailureCount: number,
  giveUpOnRepeatedMisses: boolean = false,
): number | false {
  if (!shouldPoll) return false;
  if (status === "PROCESSED" || status === "FAILED") return false;
  if (giveUpOnRepeatedMisses && fetchFailureCount >= MAX_REPORT_POLL_FAILURES) return false;
  return REPORT_POLL_INTERVAL_MS;
}

/** Report types cached at language="" and never deleted by a refine. */
const SOURCE_LANGUAGE_REPORT_TYPES: PromptReportType[] = ["TRANSCRIPT", "SENTIMENT"];

/** Listed so the client knows they exist, but rendered outside the tab grid. */
const CASCADE_ONLY_REPORT_TYPES: PromptReportType[] = ["PLAIN_BRIEF"];

/**
 * The report list minus cascade artifacts — what the tab strip and its progress reflect.
 *
 * PLAIN_BRIEF is in the list so the Gist can be driven by fact instead of a blind probe
 * against a detail endpoint that 404s. It is not a tab, so counting it would let a pending
 * Gist keep the whole project looking unfinished.
 */
export function gridReports(reports: PromptReportListItem[] | undefined): PromptReportListItem[] {
  return (reports ?? []).filter((r) => !CASCADE_ONLY_REPORT_TYPES.includes(r.report_type));
}

/**
 * How long until the next poll of the report list, or `false` to stop.
 *
 * "Nothing is in flight" cannot be read off the rows alone. `invalidate_cached_reports`
 * deletes the persona's LLM reports and keeps TRANSCRIPT/SENTIMENT, so for the first
 * seconds after a refine the list is non-empty and every row in it is PROCESSED — which
 * used to stop polling at the exact moment regeneration began, leaving the page stale
 * until an unrelated refetch (window focus, tab switch) happened to fire.
 *
 * A finished job holding no persona reports at all is therefore treated as still
 * working, which also covers the original race this guarded: the job flipping COMPLETED
 * before Celery has created any rows.
 */
export function reportListPollInterval(
  jobStatus: JobStatus | undefined,
  reports: PromptReportListItem[] | undefined,
): number | false {
  const isJobActive = jobStatus === "PENDING" || jobStatus === "PROCESSING";
  if (isJobActive) return REPORT_POLL_INTERVAL_MS;

  const anyReportActive = reports?.some((r) => r.status === "CREATED" || r.status === "PROCESSING") ?? false;
  if (anyReportActive) return REPORT_POLL_INTERVAL_MS;

  const hasPersonaReports = reports?.some((r) => !SOURCE_LANGUAGE_REPORT_TYPES.includes(r.report_type)) ?? false;
  if (jobStatus === "COMPLETED" && !hasPersonaReports) return REPORT_POLL_INTERVAL_MS;

  return false;
}

export function usePromptReport(
  jobId: string | undefined,
  reportType: PromptReportType | null,
  shouldPoll: boolean = false,
  language?: string,
  /** Set only for reports the list cannot account for — today just the Gist. */
  giveUpOnRepeatedMisses: boolean = false,
) {
  return useQuery({
    queryKey: ["prompt-report", jobId, reportType, language],
    queryFn: async () => {
      const qs = language !== undefined ? `?language=${encodeURIComponent(language)}` : "";
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/reports/${reportType}/${qs}`);
      if (!response.ok) {
        throw new Error("Failed to fetch prompt report");
      }
      return response.json() as Promise<PromptReportDetail>;
    },
    enabled: !!jobId && !!reportType,
    refetchInterval: (query) =>
      reportPollInterval(
        shouldPoll,
        query.state.data?.status,
        query.state.fetchFailureCount,
        giveUpOnRepeatedMisses,
      ),
    retry: 1,
  });
}

// Report version snapshots (P4)
export type SnapshotTrigger = "initial" | "chat" | "refine" | "revert" | "enrichment" | "user_upload";

export interface ReportSnapshotListItem {
  id: number;
  version: number;
  label: string;
  trigger: SnapshotTrigger;
  instruction: string;
  scope: string;
  persona: string;
  created_at: string;
}

export interface ReportSnapshotDetail extends ReportSnapshotListItem {
  content: string;
  input_manifest: Record<string, unknown>;
}

export function useReportSnapshots(
  jobId: string | undefined,
  reportType: PromptReportType | null,
  language?: string,
) {
  return useQuery({
    queryKey: ["report-snapshots", jobId, reportType, language],
    queryFn: async () => {
      const qs = language !== undefined ? `?language=${encodeURIComponent(language)}` : "";
      const response = await authFetch(
        `${API_BASE_URL}/jobs/${jobId}/reports/${reportType}/snapshots/${qs}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch report snapshots");
      }
      return response.json() as Promise<ReportSnapshotListItem[]>;
    },
    enabled: !!jobId && !!reportType,
  });
}

export function useReportSnapshot(
  jobId: string | undefined,
  reportType: PromptReportType | null,
  version: number | null,
  language?: string,
) {
  return useQuery({
    queryKey: ["report-snapshot", jobId, reportType, version, language],
    queryFn: async () => {
      const qs = language !== undefined ? `?language=${encodeURIComponent(language)}` : "";
      const response = await authFetch(
        `${API_BASE_URL}/jobs/${jobId}/reports/${reportType}/snapshots/${version}/${qs}`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch report snapshot");
      }
      return response.json() as Promise<ReportSnapshotDetail>;
    },
    enabled: !!jobId && !!reportType && version != null,
  });
}

export function useRevertSnapshot(jobId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { reportType: PromptReportType; version: number; language?: string }) => {
      const qs = vars.language !== undefined ? `?language=${encodeURIComponent(vars.language)}` : "";
      const response = await authFetch(
        `${API_BASE_URL}/jobs/${jobId}/reports/${vars.reportType}/snapshots/${vars.version}/revert/${qs}`,
        { method: "POST" },
      );
      if (!response.ok) {
        throw new Error("Failed to revert report");
      }
      return response.json() as Promise<{ status: string; report_type: string; reverted_to_version: number }>;
    },
    onSuccess: (_data, vars) => {
      // The live report content changed and a new revert-snapshot was written.
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", jobId] });
      queryClient.invalidateQueries({ queryKey: ["prompt-report", jobId, vars.reportType] });
      queryClient.invalidateQueries({ queryKey: ["report-snapshots", jobId, vars.reportType] });
      queryClient.invalidateQueries({ queryKey: ["project-tree", jobId] });
    },
  });
}

// Project tree (P4 C-2) — nested job → reports → snapshots
export interface ProjectTreeSnapshot {
  id: number;
  version: number;
  label: string;
  trigger: SnapshotTrigger;
  instruction: string;
  input_sources: string[];
  role_name: string | null;
  scenario_name: string | null;
  brief_data: Record<string, unknown>;
  triggered_by_message: { id: number; instruction: string } | null;
  changelog: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input_manifest: Record<string, any> | null;
  created_at: string;
}

export interface ProjectTreeReport {
  id: number;
  report_type: PromptReportType;
  language: string;
  status: PromptReportStatus;
  updated_at: string;
  snapshot_count: number;
  snapshots: ProjectTreeSnapshot[];
}

export interface ProjectTreePersona {
  persona: string;
  display_name: string;
  vertical: string;
  // {report_type: label} in tab order — mirrors the job's report_labels so the
  // Map names/orders reports identically to the report tab bar.
  report_labels: Record<string, string>;
  reports: ProjectTreeReport[];
}

export interface ProjectTree {
  id: number;
  title: string;
  status: JobStatus;
  created_at: string;
  brief_data: Record<string, string>;
  project_notes: Array<{ id: string; text: string; created_at: string }>;
  scenario: { id: number; name: string; slug: string } | null;
  personas: ProjectTreePersona[];
}

export type ProjectNote = ProjectTree["project_notes"][number];

export function useProjectTree(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["project-tree", jobId],
    queryFn: async () => {
      const r = await authFetch(`${API_BASE_URL}/jobs/${jobId}/project-tree/`);
      if (!r.ok) throw new Error("Failed to fetch project tree");
      return r.json() as Promise<ProjectTree>;
    },
    enabled: !!jobId && enabled,
  });
}

// D-2 — Working notes. Generic PATCH /jobs/{id}/ accepts only project_notes;
// we always send the full array (the backend replaces it wholesale).
export function useUpdateProjectNotes(jobId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notes: ProjectNote[]) => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_notes: notes }),
      });
      if (!response.ok) {
        throw new Error("Failed to update project notes");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-tree", jobId] });
    },
  });
}

export function useGenerateSentiment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, focusMode }: { jobId: number; focusMode: string }) => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/generate-sentiment/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ focus_mode: focusMode }),
      });
      if (!response.ok) {
        throw new Error("Failed to generate sentiment analysis");
      }
      return response.json();
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", String(jobId)] });
    },
  });
}

// --- Refine & Regenerate ---

export interface RefinementContext {
  persona: string;
  speaker_names: string[] | null;
  recording_description: string;
  target_audience: string;
  key_terms: string;
  output_language: string;
  updated_at: string;
}

export interface RefineArgs {
  jobId: number;
  persona?: Persona;
  roleSlug?: string | null;
  scenarioId?: number | null;
  briefData?: Record<string, unknown>;
  speakerNames?: string[];
  recordingDescription?: string;
  targetAudience?: string;
  keyTerms?: string;
  outputLanguage?: string;
  scope?: string;
}

export function useRefineContext(jobId: string | undefined) {
  return useQuery({
    queryKey: ["refine-context", jobId],
    queryFn: async () => {
      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/refine/`);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch refinement context");
      }
      return response.json() as Promise<RefinementContext>;
    },
    enabled: !!jobId,
  });
}

export function useRefine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      persona,
      roleSlug,
      scenarioId,
      briefData,
      speakerNames,
      recordingDescription,
      targetAudience,
      keyTerms,
      outputLanguage,
      scope,
    }: RefineArgs) => {
      const body: Record<string, unknown> = {};
      if (persona) body.persona = persona;
      if (roleSlug) body.role_slug = roleSlug;
      if (scenarioId != null) body.scenario_id = scenarioId;
      if (briefData) body.brief_data = briefData;
      if (speakerNames && speakerNames.length > 0) body.speaker_names = speakerNames;
      if (recordingDescription) body.recording_description = recordingDescription;
      if (targetAudience) body.target_audience = targetAudience;
      if (keyTerms) body.key_terms = keyTerms;
      if (outputLanguage) body.output_language = outputLanguage;
      if (scope) body.scope = scope;

      const response = await authFetch(`${API_BASE_URL}/jobs/${jobId}/refine/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === "report_limit") {
          throw new Error(`Report limit reached (${errorData.limit ?? 50})`);
        }
        throw new Error(errorData.detail || "Failed to refine reports");
      }

      return response.json();
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      queryClient.invalidateQueries({ queryKey: ["refine-context", String(jobId)] });
      // A refine can mint a new persona/language variant — refresh the strip so it shows up.
      queryClient.invalidateQueries({ queryKey: ["available-variants", String(jobId)] });
    },
  });
}

export function useRegenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      reportType,
      language,
    }: {
      jobId: number;
      reportType: string;
      /** The language tab the caller has open. Forwarded as ?language= so the
       *  backend retries that exact row instead of falling back to the job's
       *  saved output language, which may be a different, already-fine report. */
      language?: string;
    }) => {
      const qs = language !== undefined ? `?language=${encodeURIComponent(language)}` : "";
      const response = await authFetch(
        `${API_BASE_URL}/jobs/${jobId}/reports/${reportType}/retry/${qs}`,
        { method: "POST" },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to regenerate report");
      }
      return response.json();
    },
    onSuccess: (_, { jobId, reportType, language }) => {
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", String(jobId)] });
      // Without this, the cached detail for the retried row stays FAILED — its
      // refetchInterval only polls while non-terminal, so the UI never notices
      // the retry happened until the page is left and revisited.
      queryClient.invalidateQueries({ queryKey: ["prompt-report", String(jobId), reportType, language] });
    },
  });
}

// --- AI Dialogue (chat) ---

export interface ChatMessage {
  id: number;
  role: "user" | "assistant" | "system";
  content: string;
  intent: string | null;
  action_taken: string | null;
  affected_reports: string[];
  tokens_used: number;
  created_at: string;
}

export interface ChatResult {
  action: string;
  intent: string;
  affected_reports: string[];
  summary: string;
  remaining_messages: number;
  tokens_used: number;
}

export function useJobChat(jobId: string | undefined) {
  return useQuery({
    queryKey: ["job-chat", jobId],
    queryFn: async () => {
      const r = await authFetch(`${API_BASE_URL}/jobs/${jobId}/chat/`);
      if (!r.ok) throw new Error("Failed to fetch chat history");
      return r.json() as Promise<ChatMessage[]>;
    },
    enabled: !!jobId,
  });
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobId,
      message,
      activeTab,
    }: {
      jobId: string;
      message: string;
      activeTab?: string | null;
    }) => {
      const body: Record<string, unknown> = { message };
      if (activeTab) body.active_tab = activeTab;
      const r = await authFetch(`${API_BASE_URL}/jobs/${jobId}/chat/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        if (e.error === "report_limit")
          throw new Error(`Report limit reached (${e.limit ?? 50})`);
        throw new Error(e.detail || "Failed to send message");
      }
      return r.json() as Promise<ChatResult>;
    },
    onSuccess: (data, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["job-chat", jobId] });
      if (data.action === "regenerated" || data.action === "corrected") {
        // Optimistic: mark affected reports PROCESSING so the progress UI shows instantly.
        // setQueriesData (plural) partial-matches all language variants — usePromptReports
        // keys on ["prompt-reports", jobId, language], so a 2-element setQueryData would miss.
        if (data.affected_reports?.length) {
          queryClient.setQueriesData(
            { queryKey: ["prompt-reports", jobId] },
            (old: any) =>
              old?.map((r: any) =>
                data.affected_reports.includes(r.report_type) ? { ...r, status: "PROCESSING" } : r,
              ),
          );
        }
        // Skip immediate prompt-reports refetch — it would overwrite the optimistic
        // PROCESSING above before the worker has flipped the rows. Refetch jobs now
        // (job-level metadata, doesn't touch the report cache); defer the report
        // refetch so the worker catches up first, then the 5s poll takes over.
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["prompt-reports", jobId] });
          queryClient.invalidateQueries({ queryKey: ["prompt-report", jobId] });
        }, 2000);
      }
    },
  });
}
